import { useEffect } from 'react'
import styled from 'styled-components'

import { Text } from '@/components/Form'
import FilterSelect from '@/components/Form/FilterSelect'
import Label from '@/components/Label'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown/AutoCompleteVariableInput'
import Conditions from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/Boolean/Conditions'
import { ActionConfigProps, FieldTypes, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'

const StyledText = styled(Text)`
  margin-top: 1.6rem !important;
  font-size: 0.9rem;

  &.form__field {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
  }

  label {
    grid-column: span 2;
  }

  input {
    width: 5rem;
  }

  input + span {
    margin-left: 0.5rem;
  }
`

const ParametersForm = ({
  actionConfig,
  actionIndex,
  variables,
  readOnly,
  formik
}: {
  actionConfig: ActionConfigProps
  actionIndex: number
  variables: Variable[][]
  readOnly: boolean | undefined
  formik: any
}) => {
  // TODO: how to determine if filter is needed for action (scan actionsConfig?)
  const properties = actionConfig?.parametersSchema?.properties
  const isFilter = properties?.conditions
  const isPick = actionConfig?.name === 'pick'
  const conditions = formik.values?.stepData?.actions[0]?.parameters?.conditions

  useEffect(() => {
    if (isFilter && !conditions) {
      formik.setFieldValue('stepData.actions[0].parameters.conditions', [
        {
          filters: [
            {
              leftComparator: '',
              operator: 'eq',
              rightComparator: ''
            }
          ],
          expression: ''
        }
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return Object.keys(properties).map(paramKey => {
    if (properties[paramKey].type === FieldTypes.INTEGER) {
      return (
        <StyledText
          className="field__input"
          key={paramKey}
          name={`stepData.actions[${actionIndex}].parameters.${paramKey}`}
          disabled={readOnly}
          required={true}
          label={properties[paramKey].displayName}
          isnumberinput="true"
          type="number"
          min={0}
          max={10}
          data-testid="int-transform-parameter"
        />
      )
    } else if (properties[paramKey].options) {
      return (
        <FilterSelect
          defaultValue={properties[paramKey].defaultValue}
          label={properties[paramKey].displayName}
          key={paramKey}
          disabled={readOnly}
          name={`stepData.actions[${actionIndex}].parameters.${paramKey}`}
          options={properties[paramKey].options}
          className="fill-space"
        />
      )
    } else if (isFilter) {
      return (
        <div key={paramKey}>
          <Label>{properties[paramKey].displayName}</Label>
          <p className="mt-2 text-slate-600">{properties[paramKey].description}</p>
          <Conditions
            name={`stepData.actions[${actionIndex}].parameters.${paramKey}`}
            variables={variables}
            readOnly={readOnly}
            conditions={conditions}
            leftComparatorAsTextInput={true}
            leftComparatorPlaceholder={properties[paramKey].placeholder}
          />
        </div>
      )
    } else if (isPick) {
      return (
        // @ts-expect-error
        <VariableAutoComplete
          key={paramKey}
          variables={variables
            .filter(arr => arr.some(obj => obj.name.includes(formik.values?.stepData?.inputVariable as string)))
            // save the key as the value of variable
            .map(arr =>
              arr
                .filter(obj => obj.name !== formik.values?.stepData?.inputVariable && obj.name.split('.').length < 5)
                .map(obj => {
                  return {
                    ...obj,
                    properties: undefined,
                    name: obj.name.split('.').pop()!
                  }
                })
            )}
          readOnly={readOnly}
          placeholder={properties[paramKey].placeholder}
          label={properties[paramKey].displayName}
          required={
            actionConfig.parametersSchema.required.includes(properties[paramKey]) ||
            actionConfig.parametersSchema.required.includes(paramKey)
          }
          name={`stepData.actions[${actionIndex}].parameters.${paramKey}`}
          multiple={true}
          showVariableButton={false}
          allowAdditions={true}
        />
      )
    } else {
      return (
        <VariableInput
          key={paramKey}
          variables={variables}
          readOnly={readOnly}
          placeholder={properties[paramKey].placeholder}
          label={properties[paramKey].displayName}
          validVariableTypes={[properties[paramKey].type, FieldTypes.ANY]}
          required={
            actionConfig.parametersSchema.required.includes(properties[paramKey]) ||
            actionConfig.parametersSchema.required.includes(paramKey)
          }
          name={`stepData.actions[${actionIndex}].parameters.${paramKey}`}
        />
      )
    }
  })
}

export default ParametersForm
